// =====================================================
// Transaction Review Queue Component
// Main interface for reviewing transactions
// Agent 1: Review Queue UI
// =====================================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { TransactionReviewCard } from '@/components/review/TransactionReviewCard';
import { BulkActions } from '@/components/review/BulkActions';
import {
  useReviewQueue,
  useApproveTransaction,
  useEditTransaction,
  useRejectTransaction,
  useReviewProgress,
  useVATWarnings,
} from '@/hooks/useTransactionReview';
import type { ReviewTransaction, ReviewQueueFilters } from '@/services/reviewQueueService';
import { Filter, ArrowUpDown, ChevronLeft, ChevronRight, AlertCircle, CheckCircle, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { bulkCreateBookings } from '@/services/bookingService';
import { NoReviewItemsEmptyState } from '@/components/empty-states';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';


interface TransactionReviewQueueProps {
  mandateId: string;
}

export function TransactionReviewQueue({ mandateId }: TransactionReviewQueueProps) {
  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<'all' | 'uncertain' | 'missing_vat' | 'has_receipt'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'confidence' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<Set<string>>(new Set());
  const [currentTransactionIndex, setCurrentTransactionIndex] = useState(0);

  const pageSize = 20;

  // Build filters
  const filters: ReviewQueueFilters = useMemo(() => {
    return {
      status: filterStatus,
      sortBy,
      sortOrder,
    };
  }, [filterStatus, sortBy, sortOrder]);

  // Fetch data
  const { data: transactions = [], isLoading, error, refetch } = useReviewQueue({
    mandateId,
    filters,
  });

  const { data: progress } = useReviewProgress(mandateId);

  const approveTransaction = useApproveTransaction();
  const editTransaction = useEditTransaction();
  const rejectTransaction = useRejectTransaction();

  // Pagination
  const totalPages = Math.ceil(transactions.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTransactions = useMemo(
    () => transactions.slice(startIndex, endIndex),
    [transactions, startIndex, endIndex]
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setCurrentTransactionIndex(0);
  }, [filterStatus, sortBy, sortOrder]);

  // Keyboard shortcuts (unified)
  useKeyboardShortcuts(
    {
      onApprove: () => {
        const current = paginatedTransactions[currentTransactionIndex];
        if (current) handleApprove(current.id);
      },
      onReject: () => {
        const current = paginatedTransactions[currentTransactionIndex];
        if (current) handleReject(current.id);
      },
      onNext: () => {
        if (currentTransactionIndex < paginatedTransactions.length - 1) {
          setCurrentTransactionIndex((prev) => prev + 1);
        } else if (currentPage < totalPages) {
          setCurrentPage((prev) => prev + 1);
          setCurrentTransactionIndex(0);
        }
      },
      onPrevious: () => {
        if (currentTransactionIndex > 0) {
          setCurrentTransactionIndex((prev) => prev - 1);
        } else if (currentPage > 1) {
          setCurrentPage((prev) => prev - 1);
          setCurrentTransactionIndex(pageSize - 1);
        }
      },
    },
    true
  );

  // Handlers
  const handleApprove = useCallback(async (transactionId: string) => {
    try {
      await approveTransaction.mutateAsync(transactionId);
      toast.success('Transaction approved', {
        description: 'Transaction has been approved successfully'
      });
      // Move to next transaction
      if (currentTransactionIndex < paginatedTransactions.length - 1) {
        setCurrentTransactionIndex((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve transaction', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
    }
  }, [approveTransaction, currentTransactionIndex, paginatedTransactions.length]);

  const handleEdit = async (
    transactionId: string,
    updates: {
      debit_account?: string;
      credit_account?: string;
      vat_rate?: number;
    }
  ) => {
    try {
      await editTransaction.mutateAsync({ transactionId, updates });
    } catch (error) {
      console.error('Edit error:', error);
    }
  };

  const handleReject = useCallback(async (transactionId: string) => {
    try {
      await rejectTransaction.mutateAsync(transactionId);
      toast.success('Transaction rejected', {
        description: 'Transaction has been rejected and marked for review'
      });
      // Move to next transaction
      if (currentTransactionIndex < paginatedTransactions.length - 1) {
        setCurrentTransactionIndex((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject transaction', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
    }
  }, [rejectTransaction, currentTransactionIndex, paginatedTransactions.length]);

  const toggleSelectTransaction = useCallback((transactionId: string) => {
    setSelectedTransactionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedTransactionIds.size === paginatedTransactions.length) {
      setSelectedTransactionIds(new Set());
    } else {
      setSelectedTransactionIds(new Set(paginatedTransactions.map((t) => t.id)));
    }
  }, [selectedTransactionIds.size, paginatedTransactions]);

  const clearSelection = useCallback(() => {
    setSelectedTransactionIds(new Set());
  }, []);

  // 🔥 BULK BOOKING HANDLER - Direct RPC Call
  const [isBulkBooking, setIsBulkBooking] = useState(false);
  
  const handleBulkBooking = useCallback(async () => {
    const selectedIds = Array.from(selectedTransactionIds);
    
    if (selectedIds.length === 0) {
      toast.warning('Please select transactions to book');
      return;
    }
    
    // Confirmation dialog
    if (!confirm(`Book ${selectedIds.length} selected transactions?`)) {
      return;
    }
    
    setIsBulkBooking(true);
    
    try {
      const startTime = performance.now();
      const summary = await bulkCreateBookings(selectedIds);
      const duration = performance.now() - startTime;


      toast.success(
        `✅ Successfully booked ${summary.success} of ${summary.total} transactions!`,
        { duration: 5000 }
      );

      if (summary.errors && summary.errors.length > 0) {
        toast.warning(`⚠️ ${summary.errors.length} transactions had errors`, {
          duration: 5000
        });
      }

      await refetch();
      clearSelection();
    } catch (error: any) {
      console.error('Bulk booking error:', error);
      
      toast.error(`Failed to book transactions: ${error.message}`);
    } finally {
      setIsBulkBooking(false);
    }
  }, [selectedTransactionIds, refetch, clearSelection]);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load review queue. Please try again.
          <Button variant="outline" size="sm" onClick={() => refetch()} className="ml-4">
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Professional empty state when AI did all the work!
  if (transactions.length === 0) {
    return <NoReviewItemsEmptyState />;
  }

  return (
    <div className="space-y-4">
      {/* Progress Bar */}
      {progress && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-brand-primary">Review Progress</h4>
              <span className="text-sm font-bold text-brand-primary">{progress.percentComplete}%</span>
            </div>
            <div className="w-full bg-brand-secondary rounded-full h-2 mb-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress.percentComplete}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-brand-text-secondary">
              <span>
                {progress.approved + progress.edited} reviewed • {progress.pending} pending
              </span>
              <span>
                {progress.total} total
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Sort */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Select All Checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all"
                checked={
                  paginatedTransactions.length > 0 &&
                  selectedTransactionIds.size === paginatedTransactions.length
                }
                onCheckedChange={toggleSelectAll}
              />
              <label htmlFor="select-all" className="text-sm text-brand-text-secondary cursor-pointer">
                Select All ({paginatedTransactions.length})
              </label>
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-brand-border" />

            {/* Filter Status */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-brand-text-secondary" />
              <Select value={filterStatus} onValueChange={(value: any) => setFilterStatus(value)}>
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="uncertain">Uncertain AI (&lt;80%)</SelectItem>
                  <SelectItem value="missing_vat">Missing VAT</SelectItem>
                  <SelectItem value="has_receipt">Has Receipt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-brand-text-secondary" />
              <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                <SelectTrigger className="w-[150px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="confidence">Confidence</SelectItem>
                  <SelectItem value="amount">Amount</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                className="h-9"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
            </div>

            {/* 🔥 BULK BOOKING BUTTON */}
            {selectedTransactionIds.size > 0 && (
              <>
                <div className="w-px h-6 bg-brand-border" />
                <Button
                  size="sm"
                  variant="default"
                  onClick={handleBulkBooking}
                  disabled={isBulkBooking}
                  className="h-9 bg-green-600 hover:bg-green-700"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  {isBulkBooking 
                    ? `Booking ${selectedTransactionIds.size}...` 
                    : `Book Selected (${selectedTransactionIds.size})`
                  }
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearSelection}
                  className="h-9"
                >
                  Clear Selection
                </Button>
              </>
            )}

            {/* Results Count */}
            <div className="ml-auto text-sm text-brand-text-secondary">
              Showing {startIndex + 1}-{Math.min(endIndex, transactions.length)} of {transactions.length}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Cards */}
      <div className="space-y-3">
        {paginatedTransactions.map((transaction, index) => (
          <TransactionReviewCard
            key={transaction.id}
            transaction={transaction}
            isSelected={selectedTransactionIds.has(transaction.id)}
            onApprove={() => handleApprove(transaction.id)}
            onEdit={(updates) => handleEdit(transaction.id, updates)}
            onReject={() => handleReject(transaction.id)}
            onToggleSelect={() => toggleSelectTransaction(transaction.id)}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>

              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className="w-10"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keyboard Shortcuts Help */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3">
          <div className="flex items-center gap-6 text-xs text-blue-900">
            <span className="font-semibold">Keyboard Shortcuts:</span>
            <span>
              <kbd className="px-2 py-1 bg-white rounded border border-blue-300">A</kbd> = Approve
            </span>
            <span>
              <kbd className="px-2 py-1 bg-white rounded border border-blue-300">R</kbd> = Reject
            </span>
            <span>
              <kbd className="px-2 py-1 bg-white rounded border border-blue-300">←</kbd> = Previous
            </span>
            <span>
              <kbd className="px-2 py-1 bg-white rounded border border-blue-300">→</kbd> = Next
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Floating Bar */}
      <BulkActions
        selectedCount={selectedTransactionIds.size}
        selectedTransactionIds={Array.from(selectedTransactionIds)}
        onClearSelection={clearSelection}
      />
    </div>
  );
}
